import type { SessionId } from '@/features/rpa/recorder/api/recorder-bridge';
import { z } from 'zod';

const STOP_DECISION_STORAGE_KEY = 'rpa-recorder:stop-decision';
const STOP_DECISION_EVENT = 'rpa-recorder:stop-decision';
const STOP_DECISION_CHANNEL = 'rpa-recorder-stop-decision';
const STOP_DECISION_TTL_MS = 60_000;

const decisionMetaSchema = z.object({
  createdAtMs: z.number(),
  nonce: z.string(),
});

const pendingDecisionSchema = z.object({
  kind: z.literal('pending'),
  sessionId: z.string(),
  suggestedName: z.string(),
});

const promptForNameDecisionSchema = z.object({
  kind: z.literal('prompt-for-name'),
  sessionId: z.string(),
  suggestedName: z.string(),
});

const tooShortNotSavedDecisionSchema = z.object({
  kind: z.literal('too-short-not-saved'),
  sessionId: z.string(),
});

const namingSettledDecisionSchema = z.object({
  kind: z.literal('naming-settled'),
  sessionId: z.string(),
});

const recordingStopDecisionInputSchema = z.discriminatedUnion('kind', [
  pendingDecisionSchema,
  promptForNameDecisionSchema,
  tooShortNotSavedDecisionSchema,
  namingSettledDecisionSchema,
]);

const recordingStopDecisionSchema = z.discriminatedUnion('kind', [
  pendingDecisionSchema.merge(decisionMetaSchema),
  promptForNameDecisionSchema.merge(decisionMetaSchema),
  tooShortNotSavedDecisionSchema.merge(decisionMetaSchema),
  namingSettledDecisionSchema.merge(decisionMetaSchema),
]);

export type RecordingStopDecision = Readonly<z.infer<typeof recordingStopDecisionSchema>>;
export type RecordingStopDecisionInput = Readonly<
  z.infer<typeof recordingStopDecisionInputSchema>
>;

type RecordingStopDecisionBroadcastChannel = BroadcastChannel & {
  onmessage: ((event: MessageEvent<RecordingStopDecision>) => void) | null;
  postMessage(message: RecordingStopDecision): void;
};

function canUseWindow(): boolean {
  return typeof window !== 'undefined';
}

function withMeta(input: RecordingStopDecisionInput): RecordingStopDecision {
  const validated = recordingStopDecisionInputSchema.parse(input);
  return {
    ...validated,
    createdAtMs: Date.now(),
    nonce: `${Date.now()}-${Math.random()}`,
  };
}

function parseDecision(
  value: z.input<typeof recordingStopDecisionSchema>
): RecordingStopDecision | null {
  const parsed = recordingStopDecisionSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

function parseDecisionJson(raw: string): RecordingStopDecision | null {
  try {
    return parseDecision(JSON.parse(raw) as z.input<typeof recordingStopDecisionSchema>);
  } catch {
    return null;
  }
}

function isFresh(decision: RecordingStopDecision): boolean {
  return Date.now() - decision.createdAtMs < STOP_DECISION_TTL_MS;
}

/**
 * A terminal naming decision ends the naming flow for a session — no further
 * `prompt-for-name` is honored after one is published. `too-short-not-saved`
 * skips naming because the recording was discarded; `naming-settled` skips it
 * because the user already named (or discarded) the recording in another
 * window. Both suppress a late `prompt-for-name` and clear any open prompt.
 */
export function isTerminalNamingSkippedDecision(
  decision: Pick<RecordingStopDecision, 'kind'>
) {
  return decision.kind === 'too-short-not-saved' || decision.kind === 'naming-settled';
}

function hasTerminalNamingSkippedDecision(sessionId: SessionId): boolean {
  const stored = readStoredDecision();
  return stored?.sessionId === sessionId && isTerminalNamingSkippedDecision(stored);
}

/**
 * Android in-app WebViews (ad/social browsers) with DOM storage disabled expose
 * `window.localStorage` as `null` rather than throwing on access, so reading it
 * needs an explicit null check as well as the try/catch.
 */
function getDecisionStorage(): Storage | null {
  if (!canUseWindow()) return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function readStoredDecision(): RecordingStopDecision | null {
  const raw = getDecisionStorage()?.getItem(STOP_DECISION_STORAGE_KEY);
  if (!raw) return null;
  const decision = parseDecisionJson(raw);
  return decision && isFresh(decision) ? decision : null;
}

function createDecisionDeduper(): (decision: RecordingStopDecision) => boolean {
  const seenDecisions = new Map<string, number>();

  return (decision) => {
    const now = Date.now();
    for (const [key, createdAtMs] of seenDecisions) {
      if (now - createdAtMs >= STOP_DECISION_TTL_MS) {
        seenDecisions.delete(key);
      }
    }
    const key = `${decision.sessionId}:${decision.nonce}`;
    if (seenDecisions.has(key)) return false;
    seenDecisions.set(key, decision.createdAtMs);
    return true;
  };
}

function openDecisionChannel(): RecordingStopDecisionBroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  return new BroadcastChannel(
    STOP_DECISION_CHANNEL
  ) as RecordingStopDecisionBroadcastChannel;
}

function publishTauriDecision(decision: RecordingStopDecision): void {
  void import('@tauri-apps/api/event')
    .then(({ emit }) => emit(STOP_DECISION_EVENT, decision))
    .catch(() => undefined);
}

export function publishRecordingStopDecision(
  input: RecordingStopDecisionInput
): RecordingStopDecision {
  const decision = withMeta(input);
  if (
    !isTerminalNamingSkippedDecision(decision) &&
    hasTerminalNamingSkippedDecision(decision.sessionId)
  ) {
    return decision;
  }
  if (!canUseWindow()) return decision;

  try {
    window.localStorage.setItem(STOP_DECISION_STORAGE_KEY, JSON.stringify(decision));
  } catch {
    // Best effort only; the same-window event still lets mounted consumers react.
  }
  window.dispatchEvent(
    new CustomEvent<RecordingStopDecision>(STOP_DECISION_EVENT, { detail: decision })
  );

  const channel = openDecisionChannel();
  if (channel) {
    channel.postMessage(decision);
    channel.close();
  }
  publishTauriDecision(decision);

  return decision;
}

export function readRecordingStopDecision(
  sessionId: SessionId
): RecordingStopDecision | null {
  const decision = readStoredDecision();
  return decision?.sessionId === sessionId ? decision : null;
}

export function shouldDeferRecordingNaming(sessionId: SessionId): boolean {
  if (hasTerminalNamingSkippedDecision(sessionId)) return true;
  return readRecordingStopDecision(sessionId)?.kind === 'pending';
}

export function subscribeRecordingStopDecision(
  listener: (decision: RecordingStopDecision) => void
): () => void {
  if (!canUseWindow()) return () => undefined;

  const shouldNotify = createDecisionDeduper();

  const notify = (decision: RecordingStopDecision) => {
    if (!isFresh(decision) || !shouldNotify(decision)) return;
    if (isTerminalNamingSkippedDecision(decision)) {
      listener(decision);
      return;
    }
    if (hasTerminalNamingSkippedDecision(decision.sessionId)) return;
    listener(decision);
  };

  const handleCustomEvent = (event: Event) => {
    const decision = parseDecision((event as CustomEvent<RecordingStopDecision>).detail);
    if (decision) notify(decision);
  };
  const handleStorage = (event: StorageEvent) => {
    if (event.key !== STOP_DECISION_STORAGE_KEY || !event.newValue) return;
    const decision = parseDecisionJson(event.newValue);
    if (decision) notify(decision);
  };

  const channel = openDecisionChannel();
  if (channel) {
    channel.onmessage = (event: MessageEvent<RecordingStopDecision>) => {
      const decision = parseDecision(event.data);
      if (decision) notify(decision);
    };
  }
  let tauriUnlisten: (() => void) | null = null;
  let closed = false;
  void import('@tauri-apps/api/event')
    .then(({ listen }) =>
      listen<RecordingStopDecision>(STOP_DECISION_EVENT, (event) => {
        const decision = parseDecision(event.payload);
        if (decision) notify(decision);
      })
    )
    .then((unlisten) => {
      if (closed) {
        unlisten();
        return;
      }
      tauriUnlisten = unlisten;
    })
    .catch(() => undefined);

  const replayStoredDecision = () => {
    const storedDecision = readStoredDecision();
    if (storedDecision) notify(storedDecision);
  };

  // Only replay when the window is actually waking (becoming visible). The
  // event also fires on hide, where re-reading would be wasted work (and
  // could race a concurrent write in the other webview).
  const replayOnVisible = () => {
    if (document.visibilityState === 'visible') replayStoredDecision();
  };

  replayStoredDecision();

  window.addEventListener(STOP_DECISION_EVENT, handleCustomEvent);
  window.addEventListener('storage', handleStorage);
  // The overlay webview is hidden (and possibly suspended) while recording, so
  // a Tauri emit sent then can be lost with no cross-webview storage event to
  // fall back on. Rust re-shows the overlay on stop — re-read the durable
  // decision when this window wakes; the nonce deduper makes replays a no-op
  // when the live transports already delivered it.
  window.addEventListener('focus', replayStoredDecision);
  document.addEventListener('visibilitychange', replayOnVisible);

  return () => {
    closed = true;
    window.removeEventListener(STOP_DECISION_EVENT, handleCustomEvent);
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener('focus', replayStoredDecision);
    document.removeEventListener('visibilitychange', replayOnVisible);
    channel?.close();
    tauriUnlisten?.();
  };
}

export function clearRecordingStopDecision(): void {
  if (!canUseWindow()) return;
  try {
    window.localStorage.removeItem(STOP_DECISION_STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}
